import { useState, useCallback, SyntheticEvent, useRef, useMemo, useEffect } from 'react';

import type { InputProps as StyleGuideInputProps } from '@tmapy/style-guide';

import type { OmittedFormItemProps } from '../types';

export type InputProps = StyleGuideInputProps | OmittedFormItemProps;

export type UseInputArgs<ValueType> = {
  name?: string;
  initialValue?: ValueType | null;
  validator?: Validator<ValueType>;
  validate?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  onChange?(e: SyntheticEvent, eventData?: { name?: string; value: ValueType }): void;
};

export type UseInput<ValueType> = {
  name?: string;
  value: ValueType;
  isInvalid: boolean;
  errorMessage: string;
  onChange(e: SyntheticEvent, eventData?: { name?: string; value: string }): void;
  clear(): void;
};

export type Validator<T = string> = (value: T) => string | null;

export type UseInputValidated<ValueType> = UseInput<ValueType>;

export function useInput<T extends object = {}, ValueType = string>({
  name,
  initialValue,
  validator,
  validate,
  isInvalid: isExternalInvalid,
  errorMessage: externalErrorMessage,
  onChange: onChangeCallback,
  ...rest
}: T & UseInputArgs<ValueType>): Omit<T, keyof UseInputArgs<ValueType>> &
  UseInputValidated<ValueType> {
  const [value, setValue] = useState<ValueType>(initialValue ?? ('' as unknown as ValueType));

  useEffect(() => {
    setValue(initialValue ?? ('' as ValueType));
  }, [initialValue]);

  const isInvalidRef = useRef(false);

  const onChange = useCallback(
    (e: SyntheticEvent, eventData?: { name?: string; value: ValueType }) => {
      const newValue: ValueType = eventData?.value ?? (e.target as any)?.value;

      if (!e.defaultPrevented) {
        onChangeCallback?.(e, eventData);
        if (!e.defaultPrevented) {
          setValue(newValue);
        }
      }
    },
    [onChangeCallback],
  );

  const clear = useCallback(() => {
    setValue('' as unknown as ValueType);
  }, []);

  const validationResult = useMemo(() => {
    if (validate || isInvalidRef.current) {
      const errorMessage = validator?.(value);
      const isInvalid = !!errorMessage;
      isInvalidRef.current = isInvalid;

      return {
        isInvalid,
        errorMessage: errorMessage ?? '',
      };
    }
    return {
      isInvalid: false,
      errorMessage: '',
    };
  }, [value, validator, validate]);

  return {
    ...rest,
    isInvalid: validationResult.isInvalid || isExternalInvalid || false,
    errorMessage: validationResult.errorMessage || externalErrorMessage || '',
    validator,
    name,
    value,
    onChange,
    clear,
  } as any;
}
